import type {
  SitewideOptionsNavbar,
  SitewideOptionsNewslettersList,
  SitewideOptions,
  SitewideOptionsEditorsPick,
} from "lib/types/sitewideOption";
import { fetchFromDatoProxy, fetchFromDatoProxyClientSide } from "lib/cms/dato";
import fetchNewslettersList from "lib/fetchers/siteWide/newsletters";
import { PaywallWindow } from "lib/types/paywalls";
import { Banner } from "lib/types/banners";

type NavbarResult = SitewideOptionsNavbar | null;

type NavbarAndFooterResult =
  | (SitewideOptionsNavbar & SitewideOptionsNewslettersList)
  | null;

export const fetchNavbarAndFooter =
  async (): Promise<NavbarAndFooterResult> => {
    const [data, newslettersList] = await Promise.all([
      fetchFromDatoProxy<{ sitewideOption: NavbarResult }>({
        query: `
    query Query {
      sitewideOption {
        footerCopyright
        footerExtraList {
          id
          label
          url
          description
        }
        footerPrimaryList {
          id
          label
          url
          description
        }
        footerSecondaryList {
          id
          label
          url
          description
        }
        footerCommunityList {
          id
          label
          url
          description
        }
        footerExtraListColumnLabel
        footerPrimaryListColumnLabel
        footerSecondaryListColumnLabel
        footerCommunityListColumnLabel
        footerSocials {
          id
          image {
            alt
            id
            height
            title
            width
            url
            blurUpThumb
          }
          label
          url
        }
        logInUrl
        logInLabel
        logoutLabel
        navbarPrimaryLinks {
          description
          id
          label
          url
          list {
            id
            label
            url
            description
          }
        }
        searchLabel
        searchUrl
        sidebarHeading
        sidebarPrimaryLinks {
          id
          label
          list {
            description
            id
            label
            url
          }
          url
          description
        }
        subscribeCtaLabel
        subscribeCtaLabelMobile
        subscribeCtaUrl
        userProfileLabel
        userProfileUrl
        logoOverwrite {
          url
          alt
          width
          height
        }
      }
    }
  `,
        urlPath: "/sitewideOptions/menus",
      }),
      fetchNewslettersList(),
    ]);

    if (!data) {
      return null;
    }

    const logoData = data.sitewideOption?.logoOverwrite;

    return {
      ...data.sitewideOption,
      newslettersList,
      logo: logoData
        ? {
            overwriteUrl: logoData.url || null,
            alt: logoData.alt || null,
            width: logoData.width ?? null,
            height: logoData.height ?? null,
          }
        : {
            overwriteUrl: null,
            alt: null,
            width: null,
            height: null,
          },
    };
  };

type FetchEditorsPickResponse = {
  sitewideOption: SitewideOptions["editorsPickArticles"] &
    SitewideOptions["editorsPickHeading"];
};

export const fetchEditorsPick =
  async (): Promise<SitewideOptionsEditorsPick | null> => {
    const data = await fetchFromDatoProxy<FetchEditorsPickResponse | null>({
      query: `
    query MyQuery {
      sitewideOption {
        editorsPickHeading
        editorsPickArticles {
          id
          title
          articleSlug
        }
      }
    }
  `,
      urlPath: "/sitewideOptions/editorsPick",
    });

    if (!data) return null;

    return { ...data.sitewideOption };
  };

type FetchPaywallByIdResponse = {
  paywallWindow: PaywallWindow;
};

type FetchPaywalls = {
  allPaywallWindows: PaywallWindow[];
};

type FetchPaywallByIdParameters = {
  id: string;
  preview: boolean;
};
export const fetchPaywallById = async ({
  id,
  preview,
}: FetchPaywallByIdParameters): Promise<PaywallWindow | null> => {
  const data =
    await fetchFromDatoProxyClientSide<FetchPaywallByIdResponse | null>({
      query: `
      query PaywallWindowQuery {
        paywallWindow(filter: {id: {eq: "${id}"}}) {
          id
          name
          additionalButtonLabel
          additionalButtonUrl
          dismissButtonUrl
          dismissButtonLabel
          loginCta
          loginLabel
          secondaryButtonUrl
          primaryButtonLabel
          primaryButtonUrl
          subtitle
          theme
          title
          titleColour
        }
      }
  `,
      urlPath: `/paywall/${id}`,
      preview,
    });

  return data?.paywallWindow || null;
};

export const fetchPaywallByIdServer = async (
  id?: string
): Promise<PaywallWindow | null> => {
  const data = await fetchFromDatoProxy<FetchPaywallByIdResponse | null>({
    query: `
      query PaywallWindowQuery {
        paywallWindow(filter: {id: {eq: "${id}"}}) {
          id
          name
          additionalButtonLabel
          additionalButtonUrl
          dismissButtonUrl
          dismissButtonLabel
          loginCta
          loginLabel
          secondaryButtonUrl
          primaryButtonLabel
          primaryButtonUrl
          subtitle
          theme
          title
          titleColour
        }
      }
  `,
    urlPath: `/paywall/${id}`,
  });

  return data?.paywallWindow || null;
};

export const fetchAllPaywalls = async (): Promise<PaywallWindow[] | null> => {
  const query = `
      query PaywallWindowQuery {
        allPaywallWindows {
          id
          name
          additionalButtonLabel
          additionalButtonUrl
          dismissButtonUrl
          dismissButtonLabel
          loginCta
          loginLabel
          secondaryButtonUrl
          primaryButtonLabel
          primaryButtonUrl
          subtitle
          theme
          title
          titleColour
        }
      }
  `;

  const data = await fetchFromDatoProxy<FetchPaywalls | null>({
    query: query,
    urlPath: `/all-paywalls`,
  });

  return data?.allPaywallWindows || null;
};

type FetchBannerById = {
  id: string;
  preview: boolean;
};

type FetchBannerByIdResponse = {
  banner: Banner;
};

export const fetchBannerById = async ({
  id,
  preview,
}: FetchBannerById): Promise<Banner | null> => {
  const data =
    await fetchFromDatoProxyClientSide<FetchBannerByIdResponse | null>({
      query: `
      query PaywallWindowQuery {
        banner(filter: {id: {eq: "${id}"}}) {
          id
          name
          primaryButtonLabel
          primaryButtonUrl
          heading
          text
          theme
          mainImage {
            alt
            id
            height
            title
            width
            url
            blurUpThumb
          }
        }
      }
  `,
      urlPath: `/banner/${id}`,
      preview,
    });

  return data?.banner || null;
};

type FetchChatbotConfigResponse = {
  sitewideOption: {
    chatbotIcon?: {
      alt: string;
      id: string;
      height: number;
      title: string;
      width: number;
      url: string;
      blurUpThumb: string;
    };
  };
};

export const fetchChatbotConfig = async (
  preview: boolean = false
): Promise<Pick<SitewideOptions, "chatbot"> | null> => {
  const data =
    await fetchFromDatoProxyClientSide<FetchChatbotConfigResponse | null>({
      query: `
    query ChatbotConfigQuery {
      sitewideOption {
        chatbotIcon {
          alt
          id
          height
          title
          width
          url
          blurUpThumb
        }
      }
    }
  `,
      urlPath: "/sitewideOptions/chatbot",
    });

  if (!data || !data.sitewideOption || !data.sitewideOption.chatbotIcon) {
    return null;
  }

  return {
    chatbot: {
      icon: data.sitewideOption.chatbotIcon,
    },
  };
};
